import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { loadSiteConfigRequest, saveSiteConfigRequest } from "queries/site-config-queries";
import { useUserData } from "store/user-data";
import { Section } from "./parts";

const PIXEL_ID = /^\d{15,16}$/;
const EVENTS = ["ViewContent", "AddToCart", "InitiateCheckout", "Purchase"];

/** Lets a vendor who runs their own Meta ads add their Pixel, which the storefront then loads. */
export default function PixelCard() {
  const { userData } = useUserData();
  const businessId = userData?.business_id;
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: config, isLoading } = useQuery({
    queryKey: ["site-config", businessId],
    queryFn: () => loadSiteConfigRequest(businessId!),
    enabled: !!businessId,
    retry: 1,
  });
  const connected: string = config?.metadata?.metaPixelId || "";
  const valid = PIXEL_ID.test(draft);

  const save = async (pixelId: string) => {
    setSaving(true);
    try {
      // Saving replaces metadata as a whole, so keep whatever else is in it.
      const metadata = { ...(config?.metadata || {}), metaPixelId: pixelId };
      const saved = await saveSiteConfigRequest(businessId!, { metadata });
      queryClient.setQueryData(["site-config", businessId], saved);
      setDraft("");
      toast.success(pixelId ? "Pixel connected. Your store now reports to it." : "Pixel disconnected.");
    } catch (error: any) {
      toast.error(error?.validationErrors?.message || error?.message || "Couldn't save your Pixel.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Connect your own Meta Pixel">
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <p className="text-sm text-default-600">
            Running your own ads in Meta Ads Manager? Add your Pixel ID and your store will tell Meta when people view a
            product, add to cart, start checkout and buy, so you can measure and target your ads.
          </p>
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin text-default-400" />
          ) : connected ? (
            <div className="flex flex-col gap-3 rounded-[14px] bg-success/10 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-success" />
                <div>
                  <div className="text-sm font-medium text-default-900">Connected</div>
                  <div className="text-xs tabular-nums text-default-600">Pixel ID {connected}</div>
                </div>
              </div>
              <Button size="md" variant="outline" disabled={saving} onClick={() => save("")}>
                Disconnect
              </Button>
            </div>
          ) : (
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(event) => {
                event.preventDefault();
                if (valid) save(draft);
              }}
            >
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value.replace(/\D/g, "").slice(0, 16))}
                inputMode="numeric"
                placeholder="e.g. 1234567890123456"
                aria-label="Meta Pixel ID"
                className="sm:max-w-xs"
              />
              <Button type="submit" disabled={!valid || saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Connect Pixel
              </Button>
            </form>
          )}
          {!connected && draft && !valid && (
            <p className="text-xs text-default-500">A Pixel ID is 15 or 16 digits.</p>
          )}
        </div>
        <div className="rounded-[14px] bg-default-50 p-4 text-sm">
          <div className="font-medium text-default-900">Where to find it</div>
          <p className="mt-1 text-default-600">
            In Meta Events Manager, open <span className="font-medium">Data sources</span>, choose your Pixel and copy the
            number under its name.
          </p>
          <div className="mt-3 font-medium text-default-900">Events your store sends</div>
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {EVENTS.map((event) => (
              <li key={event} className="rounded-full bg-card px-2.5 py-1 text-xs text-default-700">
                {event}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}
